import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Pagination } from '../../../model/paginatiion';
import { IPublicAccountResponse } from '../../../model/base/response/public-account-response';
import { ICategoryWithSubcategoriesResponse } from '../../../model/base/response/category-with-subcategories-response';

interface PublicAccountsState {
  publicAccounts: Pagination<IPublicAccountResponse>;
  categories: ICategoryWithSubcategoriesResponse[];
  loading: boolean;
  loadingFilters: boolean;
  error: string | null;
}

type PublicAccountsAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: Pagination<IPublicAccountResponse> }
  | { type: 'LOAD_ERROR'; payload: string }
  | { type: 'LOAD_CATEGORIES_START' }
  | { type: 'LOAD_CATEGORIES_SUCCESS'; payload: ICategoryWithSubcategoriesResponse[] }
  | { type: 'LOAD_CATEGORIES_ERROR'; payload: string };

const initialState: PublicAccountsState = {
  publicAccounts: new Pagination<IPublicAccountResponse>(),
  categories: [],
  loading: false,
  loadingFilters: false,
  error: null,
};

const PublicAccountsStateContext = createContext<PublicAccountsState | undefined>(undefined);
const PublicAccountsDispatchContext = createContext<React.Dispatch<PublicAccountsAction> | undefined>(undefined);

function publicAccountsReducer(state: PublicAccountsState, action: PublicAccountsAction): PublicAccountsState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, publicAccounts: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'LOAD_CATEGORIES_START':
      return { ...state, loadingFilters: true };
    case 'LOAD_CATEGORIES_SUCCESS':
      return { ...state, loadingFilters: false, categories: action.payload };
    case 'LOAD_CATEGORIES_ERROR':
      return { ...state, loadingFilters: false, error: action.payload };
    default:
      return state;
  }
}

export function PublicAccountsProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(publicAccountsReducer, initialState);

  return (
    <PublicAccountsStateContext.Provider value={state}>
      <PublicAccountsDispatchContext.Provider value={dispatch}>
        {children}
      </PublicAccountsDispatchContext.Provider>
    </PublicAccountsStateContext.Provider>
  );
}

export function usePublicAccountsState() {
  const context = useContext(PublicAccountsStateContext);
  if (context === undefined) {
    throw new Error('usePublicAccountsState must be used within a PublicAccountsProvider');
  }
  return context;
}

export function usePublicAccountsDispatch() {
  const context = useContext(PublicAccountsDispatchContext);
  if (context === undefined) {
    throw new Error('usePublicAccountsDispatch must be used within a PublicAccountsProvider');
  }
  return context;
}
